/**
 * Web Vitals tracking and reporting
 *
 * Tracks Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint)
 * - CLS (Cumulative Layout Shift)
 * - INP (Interaction to Next Paint)
 * - TTFB (Time to First Byte)
 */

interface WebVitalMetric {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
}

interface VitalsReport {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
}

/**
 * Reports Web Vitals to analytics or console
 *
 * @example
 * // In main.tsx
 * reportWebVitals((metric) => {
 *   // Send to analytics
 *   gtag('event', metric.name, { value: metric.value });
 * });
 */
export async function reportWebVitals(onReport?: (metric: VitalsReport) => void) {
    // Dynamic import to keep bundle size small if not needed.
    //
    // Guarded: callers (e.g. root.tsx) invoke this without awaiting, so a
    // failed import would surface as an *unhandled* promise rejection. That
    // fires routinely on the bare-root `/` page, whose immediate
    // `window.location.replace('/en/')` aborts the in-flight chunk request
    // (NS_BINDING_ABORTED) — harmless, but it pollutes the console and the
    // OTEL error stream. Swallow it; RUM is best-effort.
    let vitals: typeof import('web-vitals');
    try {
        vitals = await import('web-vitals');
    } catch (err) {
        if (import.meta.env.DEV) {
            console.warn('[Web Vitals] module failed to load; skipping RUM', err);
        }
        return;
    }
    const { onCLS, onLCP, onINP, onTTFB } = vitals;

    const reportHandler = (metric: WebVitalMetric) => {
        const report: VitalsReport = {
            name: metric.name,
            value: metric.value,
            rating: metric.rating,
            delta: metric.delta,
            id: metric.id,
        };

        if (onReport) {
            onReport(report);
        }

        // Log to console in development
        if (import.meta.env.DEV) {
            const color = metric.rating === 'good'
                ? '#0cce6b'
                : metric.rating === 'needs-improvement'
                    ? '#ffa400'
                    : '#ff4e42';

            console.log(
                `%c[Web Vital] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
                `color: ${color}; font-weight: bold;`
            );
        }
    };

    onCLS(reportHandler);
    onLCP(reportHandler);
    onINP(reportHandler);
    onTTFB(reportHandler);
}

/**
 * Get thresholds for each metric
 * Based on Google's Core Web Vitals thresholds
 * Note: FID is deprecated in favor of INP as of March 2024
 */
export const webVitalThresholds = {
    LCP: { good: 2500, poor: 4000 },
    CLS: { good: 0.1, poor: 0.25 },
    INP: { good: 200, poor: 500 },
    TTFB: { good: 800, poor: 1800 },
} as const;

/**
 * Get rating for a metric value
 */
export function getMetricRating(
    name: keyof typeof webVitalThresholds,
    value: number
): 'good' | 'needs-improvement' | 'poor' {
    const thresholds = webVitalThresholds[name];
    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.poor) return 'needs-improvement';
    return 'poor';
}
